import { createFileRoute, Link } from '@tanstack/react-router'

// Public account-deletion page. App Store and Google Play both ask for a web
// address where people can learn how to delete their account and data.
export const Route = createFileRoute('/_site/delete-account')({
  component: DeleteAccountPage,
  head: () => ({
    meta: [
      { title: 'Delete Your Uptrack Account — App, Website or Email' },
      { name: 'description', content: 'How to delete your Uptrack account and data from the app, the website or by email, and what we delete.' },
      { property: 'og:title', content: 'Delete Your Uptrack Account — App, Website or Email' },
      { property: 'og:description', content: 'How to delete your Uptrack account and data.' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/delete-account' },
    ],
  }),
})

function DeleteAccountPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <div className="mb-4">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          &larr; Back to home
        </Link>
      </div>
      <h1 className="mb-8 text-3xl font-semibold">Delete your Uptrack account</h1>
      <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
        <p className="text-sm">Last updated: October 7, 2026</p>

        <section>
          <h2 className="text-xl font-semibold text-foreground">1. In the mobile app (iOS and Android)</h2>
          <p>
            Open <strong className="text-foreground">Settings</strong>, scroll to{' '}
            <strong className="text-foreground">Danger zone</strong> and tap{' '}
            <strong className="text-foreground">Delete account</strong>. For safety, the app deletes an
            account only within 15 minutes of a sign-in. If you signed in earlier, the app asks you to
            sign in again first.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">2. On the website</h2>
          <p>
            Sign in at{' '}
            <Link to="/login" className="text-primary hover:underline">uptrack.app</Link>, open{' '}
            <strong className="text-foreground">Settings</strong> and choose{' '}
            <strong className="text-foreground">Delete account</strong>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">3. By email</h2>
          <p>
            If you cannot sign in, email{' '}
            <a href="mailto:support@uptrack.app?subject=Delete%20my%20account" className="text-primary hover:underline">
              support@uptrack.app
            </a>{' '}
            from the address on your account and ask us to delete it. We aim to reply within 5 business days.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">4. What we delete, and when</h2>
          <p>
            Your account is deactivated at once, and you can ask support to restore it for 30 days.
            After 30 days we permanently delete your account, your organization, your monitors, alert
            settings, incidents and check results. Encrypted database backups expire on a rolling schedule, so the deleted data
            leaves our backups within 35 more days.
          </p>
          <p>
            Invoices are kept by Paddle, our payment provider, because the law requires it. See the{' '}
            <Link to="/privacy" className="text-primary hover:underline">privacy policy</Link> for details.
          </p>
        </section>
      </div>
    </div>
  )
}
